import React from "react";

import { register } from "../../../api";
import { apiFetch } from "../../../apiClient";
import { buildPlanKey, parsePlanKey, PLAN_KEYS } from "../../../subscription";
import { ADDRESS_REQUIRED_MESSAGES } from "../../../utils/addressData";
import { getEmailError } from "../../../utils/contactValidation";

const DEFAULT_FORM = {
    firstname: "",
    lastname: "",
    username: "",
    email: "",
    street: "",
    houseNumber: "",
    zip: "",
    city: "",
    password: "",
    confirmPassword: "",
    paymentPlan: PLAN_KEYS.ACCOUNTING_MONTHLY,
    registerAsAdmin: false,
    paidUntil: "",
};

export default function useRegistrationForm({ navigate }) {
    const [formData, setFormData] = React.useState(DEFAULT_FORM);
    const [lastStandardPlan, setLastStandardPlan] = React.useState(PLAN_KEYS.ACCOUNTING_MONTHLY);
    const [showInclVat, setShowInclVat] = React.useState(false);
    const [showPw, setShowPw] = React.useState(false);
    const [showPw2, setShowPw2] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState("");
    const [fieldErrors, setFieldErrors] = React.useState({});
    const [testUserEnabled, setTestUserEnabled] = React.useState(false);
    const [adminRegistrationAvailable, setAdminRegistrationAvailable] = React.useState(false);
    const [publicUserRegistrationEnabled, setPublicUserRegistrationEnabled] = React.useState(true);

    React.useEffect(() => {
        apiFetch("/api/system/config", { suppressAuthError: true })
            .then((res) => res.json())
            .then((data) => {
                setPublicUserRegistrationEnabled(data.publicUserRegistrationEnabled !== false);
                if (data.testUserRegistrationEnabled) {
                    setTestUserEnabled(true);
                }
                const adminAvailable = Boolean(data.adminUserRegistrationAvailable);
                setAdminRegistrationAvailable(adminAvailable);
                if (!adminAvailable) {
                    setFormData((state) => ({ ...state, registerAsAdmin: false }));
                }
            })
            .catch((err) => console.error("Failed to fetch system config", err));
    }, []);

    React.useEffect(() => {
        if (formData.paymentPlan !== PLAN_KEYS.TEST) {
            setLastStandardPlan(formData.paymentPlan);
        }
    }, [formData.paymentPlan]);

    const isTestPlan = formData.paymentPlan === PLAN_KEYS.TEST;
    const showTestPlanOption = testUserEnabled || formData.registerAsAdmin;
    const { tier, cycle } = isTestPlan
        ? { tier: "TEST", cycle: "individuell" }
        : parsePlanKey(formData.paymentPlan);

    React.useEffect(() => {
        if (!isTestPlan && fieldErrors.paidUntil) {
            setFieldErrors((prev) => {
                const next = { ...prev };
                delete next.paidUntil;
                return next;
            });
        }
    }, [isTestPlan, fieldErrors.paidUntil]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((state) => ({ ...state, [name]: value }));
        if (fieldErrors[name]) {
            setFieldErrors((prev) => {
                const next = { ...prev };
                delete next[name];
                return next;
            });
        }
        if (error) {
            setError("");
        }
    };

    const handleAdminToggle = (event) => {
        const checked = event.target.checked;
        setFormData((state) => ({
            ...state,
            registerAsAdmin: checked,
            paymentPlan: checked ? PLAN_KEYS.TEST : lastStandardPlan,
        }));
    };

    const handleTierSelect = (value) => {
        if (formData.registerAsAdmin && value !== PLAN_KEYS.TEST) {
            return;
        }
        setFormData((state) => {
            if (value === PLAN_KEYS.TEST) {
                return { ...state, paymentPlan: PLAN_KEYS.TEST };
            }
            const { cycle: currentCycle } = parsePlanKey(
                state.paymentPlan === PLAN_KEYS.TEST ? PLAN_KEYS.INVOICE_MONTHLY : state.paymentPlan
            );
            return { ...state, paymentPlan: buildPlanKey(value, currentCycle) };
        });
    };

    const handleCycleSelect = (value) => {
        if (isTestPlan) return;
        setFormData((state) => {
            const { tier: currentTier } = parsePlanKey(state.paymentPlan);
            return { ...state, paymentPlan: buildPlanKey(currentTier, value) };
        });
    };

    const handlePaidUntilChange = (date) => {
        setFormData((state) => ({ ...state, paidUntil: date }));
        if (fieldErrors.paidUntil) {
            setFieldErrors((prev) => {
                const next = { ...prev };
                delete next.paidUntil;
                return next;
            });
        }
        if (error) {
            setError("");
        }
    };

    const validate = () => {
        const nextErrors = {};
        if (!formData.firstname?.trim()) nextErrors.firstname = "Bitte Vornamen eingeben.";
        if (!formData.lastname?.trim()) nextErrors.lastname = "Bitte Nachnamen eingeben.";
        if (!formData.username?.trim()) nextErrors.username = "Bitte Benutzernamen eingeben.";
        const emailError = getEmailError(formData.email, { required: true });
        if (emailError) {
            nextErrors.email = emailError;
        }
        if (!formData.street?.trim()) nextErrors.street = ADDRESS_REQUIRED_MESSAGES.street;
        if (!formData.houseNumber?.trim()) nextErrors.houseNumber = "Bitte Hausnummer eingeben.";
        if (!formData.zip?.trim()) nextErrors.zip = ADDRESS_REQUIRED_MESSAGES.zip;
        if (!formData.city?.trim()) nextErrors.city = ADDRESS_REQUIRED_MESSAGES.city;
        if (isTestPlan && !formData.paidUntil) {
            nextErrors.paidUntil = "Bitte ein Enddatum für den Test-User angeben.";
        }
        if (formData.password.length < 8) {
            nextErrors.password = "Passwort muss mindestens 8 Zeichen lang sein.";
        }
        if (!formData.confirmPassword?.trim()) {
            nextErrors.confirmPassword = "Bitte Passwort bestätigen.";
        } else if (formData.password !== formData.confirmPassword) {
            nextErrors.confirmPassword = "Passwörter stimmen nicht überein.";
        }
        return nextErrors;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setFieldErrors(validationErrors);
            setError("Bitte prüfe die markierten Felder.");
            return;
        }
        setFieldErrors({});
        setError("");
        setLoading(true);
        try {
            const resp = await register(formData);
            if (resp.status === 200 || resp.status === 201) {
                if (resp.data?.paymentUrl) {
                    window.location.href = resp.data.paymentUrl;
                } else {
                    navigate("/registrationSuccess");
                }
            } else {
                setError("Registrierung fehlgeschlagen. Bitte später erneut versuchen.");
            }
        } catch (err) {
            const backendError = err?.response?.data;
            const details = Array.isArray(backendError?.details) ? backendError.details : null;
            if (details && details.length > 0) {
                const nextErrors = {};
                details.forEach((detail) => {
                    const rawField = detail?.field;
                    const message = detail?.message;
                    if (!rawField || !message) return;
                    const field = rawField.split(".").pop();
                    nextErrors[field] = message;
                });
                if (Object.keys(nextErrors).length > 0) {
                    setFieldErrors(nextErrors);
                    setError(backendError?.message || "Bitte prüfe die markierten Felder.");
                    return;
                }
            }
            const msg =
                typeof backendError === "string"
                    ? backendError
                    : backendError?.message || err?.message || "Es ist ein Fehler bei der Registrierung aufgetreten.";
            setError(String(msg));
        } finally {
            setLoading(false);
        }
    };

    return {
        formData,
        setFormData,
        showInclVat,
        setShowInclVat,
        showPw,
        setShowPw,
        showPw2,
        setShowPw2,
        loading,
        error,
        fieldErrors,
        testUserEnabled,
        adminRegistrationAvailable,
        publicUserRegistrationEnabled,
        isTestPlan,
        showTestPlanOption,
        tier,
        cycle,
        handleChange,
        handleAdminToggle,
        handleTierSelect,
        handleCycleSelect,
        handlePaidUntilChange,
        handleSubmit,
    };
}
